/**
 * Derived, state-specific context for the /easement-law/$state pages.
 *
 * The per-state pages were thin near-duplicates of one another: a period, a
 * citation and three bullets. Everything here is computed from the existing
 * datasets (no new data provider, no paid source) so each page carries facts
 * that are true only for that state — how its period compares with the rest of
 * the country, which counties we have local guides for, where instruments get
 * recorded, and a Q&A block written from that state's own numbers.
 */
import {
  COUNTY_EASEMENT_GUIDES,
  countyGuidePath,
  type CountyEasementGuide,
} from "@/data/countyEasementGuides";
import {
  countyRecordingRuleFor,
  type CountyRecordingRule,
} from "@/data/countyRecordingRules";
import {
  PRESCRIPTIVE_EASEMENT_LAW_SORTED,
  type StateEasementLaw,
} from "@/data/prescriptiveEasementLaw";
import { HOMEOWNER_QUESTIONS, type HomeownerQuestion } from "@/data/homeowner-questions";
import { PUBLIC_RECORDS_STATES, publicRecordsStatePath } from "@/lib/public-records-routes";
import {
  statutoryExcerptFor,
  type StatuteExcerptKind,
} from "@/data/stateStatutoryExcerpts";
import {
  casesForState,
  type EasementValuationCase,
} from "@/data/easementValuationCases";
import {
  enhancedStateLawProfileFor,
  type EnhancedStateLawProfile,
} from "@/data/enhancedStateLawProfiles";

export interface StatePeriodComparison {
  /** Median prescriptive period across all states. */
  median: number;
  /** Shortest and longest periods in the dataset. */
  shortest: number;
  longest: number;
  /** Number of states sharing this state's period. */
  sharing: number;
  /** How many states have a shorter period. */
  shorterStates: number;
  /** "shorter than", "the same as" or "longer than" the national median. */
  relation: "shorter" | "typical" | "longer";
  /** Full sentence describing where the state sits. */
  sentence: string;
}

const ALL_YEARS = PRESCRIPTIVE_EASEMENT_LAW_SORTED.map((law) => law.years).sort((a, b) => a - b);

function median(values: number[]): number {
  const mid = Math.floor(values.length / 2);
  if (values.length % 2 === 1) return values[mid]!;
  return (values[mid - 1]! + values[mid]!) / 2;
}

export function periodComparison(law: StateEasementLaw): StatePeriodComparison {
  const med = median(ALL_YEARS);
  const shortest = ALL_YEARS[0]!;
  const longest = ALL_YEARS[ALL_YEARS.length - 1]!;
  const sharing = ALL_YEARS.filter((y) => y === law.years).length;
  const shorterStates = ALL_YEARS.filter((y) => y < law.years).length;
  const relation: StatePeriodComparison["relation"] =
    law.years < med ? "shorter" : law.years > med ? "longer" : "typical";

  const parts: string[] = [];
  if (relation === "shorter") {
    parts.push(
      `${law.stateName} runs a shorter clock than most of the country: ${law.years} years against a ${med}-year national median, so a neighbor's driveway, path or fence line can harden into a legal right here faster than it would in most states.`,
    );
  } else if (relation === "longer") {
    parts.push(
      `${law.stateName} runs a longer clock than most of the country: ${law.years} years against a ${med}-year national median, so use that started recently is unlikely to have ripened into a prescriptive right yet.`,
    );
  } else {
    parts.push(
      `At ${law.years} years, ${law.stateName} sits right at the national median, which is also ${med} years.`,
    );
  }
  parts.push(
    `${sharing === 1 ? "No other state uses" : `${sharing} states in total use`} a ${law.years}-year period${sharing === 1 ? ". It is unique to " + law.stateName : ""}.`,
  );
  parts.push(
    `Nationally the periods run from ${shortest} to ${longest} years, and ${shorterStates} states are quicker than ${law.stateName}.`,
  );

  return {
    median: med,
    shortest,
    longest,
    sharing,
    shorterStates,
    relation,
    sentence: parts.join(" "),
  };
}

/** County guides we publish for this state, alphabetical. */
export function countyGuidesForState(law: StateEasementLaw): CountyEasementGuide[] {
  return COUNTY_EASEMENT_GUIDES.filter((g) => g.stateCode === law.stateCode).sort((a, b) =>
    a.county.localeCompare(b.county),
  );
}

export interface CountyRecordingSection {
  guide: CountyEasementGuide;
  rule: CountyRecordingRule | null;
  /** Office name, preferring the county's own published wording. */
  office: string;
  /** Fee sentence: county-specific where researched, otherwise state baseline. */
  feeSentence: string;
  /** Formatting bullets, county-specific when researched. */
  formatting: string[];
  /** True when the copy comes from the county's own published rules. */
  verified: boolean;
}

/**
 * County-by-county recording rules for a state page. Counties we have
 * researched carry their own office rules; the rest fall back to the state
 * recording baseline rather than inventing local requirements.
 */
export function countyRecordingSectionsForState(law: StateEasementLaw): CountyRecordingSection[] {
  const statute = statutoryExcerptFor(law.stateCode);
  return countyGuidesForState(law).map((guide) => {
    const rule = countyRecordingRuleFor(law.stateCode, guide.slug);
    const baselineFee =
      guide.feeNote ??
      statute?.recordingFeeNote ??
      `${law.stateName} sets recording charges at the county level; the recording office publishes the current schedule.`;
    return {
      guide,
      rule,
      office: rule?.office ?? guide.recordingOffice,
      feeSentence: rule?.feeStructure ?? baselineFee,
      formatting: rule?.formatting ?? [],
      verified: Boolean(rule),
    };
  });
}

/** Path to this state's public-records page, when we cover it. */
export function publicRecordsPathForState(law: StateEasementLaw): string | null {
  const covered = PUBLIC_RECORDS_STATES.some(
    (s) => s.code.toLowerCase() === law.stateCode.toLowerCase(),
  );
  return covered ? publicRecordsStatePath(law.stateCode) : null;
}

/** Recording offices we can name for this state, deduped. */
export function recordingOfficesForState(law: StateEasementLaw): string[] {
  return Array.from(new Set(countyGuidesForState(law).map((g) => g.recordingOffice)));
}

/** Easement types that dominate local title work in this state's counties. */
export function commonEasementsForState(law: StateEasementLaw): string[] {
  const counts = new Map<string, number>();
  for (const guide of countyGuidesForState(law)) {
    for (const kind of guide.commonEasements) {
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 6)
    .map(([kind]) => kind);
}

/** Homeowner questions worth linking from a state page, deterministic order. */
export function questionsForState(): HomeownerQuestion[] {
  const preferred = ["Disputes & neighbors", "Driveways & access", "Finding easements"];
  return HOMEOWNER_QUESTIONS.filter((q) => preferred.includes(q.category))
    .sort((a, b) => a.slug.localeCompare(b.slug))
    .slice(0, 4);
}

export interface StateFaqEntry {
  question: string;
  answer: string;
}

/** Q&A written from this state's own numbers, notes and county coverage. */
export function stateFaq(law: StateEasementLaw): StateFaqEntry[] {
  const comparison = periodComparison(law);
  const counties = countyGuidesForState(law);
  const offices = recordingOfficesForState(law);
  const gisCounties = counties.filter((c) => c.publicGis);

  const faq: StateFaqEntry[] = [
    {
      question: `How long does it take to create a prescriptive easement in ${law.stateName}?`,
      answer: `${law.years} years of open, notorious, continuous and adverse use is the period generally applied in ${law.stateName}, commonly cited to ${law.citation}. The clock only runs while the use is visible and without the owner's permission; written permission restarts it.`,
    },
    {
      question: `Is ${law.stateName}'s prescriptive period longer than other states?`,
      answer: comparison.sentence,
    },
  ];

  if (law.adversePossessionYears) {
    faq.push({
      question: `Is adverse possession the same as a prescriptive easement in ${law.stateName}?`,
      answer: `No. A prescriptive easement gives someone the right to keep using part of your land after ${law.years} years; adverse possession transfers ownership outright and runs on a ${law.adversePossessionYears}-year clock in ${law.stateName}. A driveway claim is almost always the first; a fenced-in strip of yard can become the second.`,
    });
  } else {
    faq.push({
      question: `Can someone take ownership of my land in ${law.stateName}, not just a right to cross it?`,
      answer: `A prescriptive easement only creates a right of use after ${law.years} years. You keep title. Ownership claims run through ${law.stateName}'s separate adverse possession rules, which require possession of the strip itself rather than a shared use of it.`,
    });
  }

  if (law.notes.length > 0) {
    faq.push({
      question: `What has to be proven for an easement claim in ${law.stateName}?`,
      answer: law.notes.join(" "),
    });
  }

  faq.push({
    question: `Where are easements recorded in ${law.stateName}?`,
    answer:
      offices.length > 0
        ? `Easements are recorded at the county level. For the ${law.stateName} counties we cover that means ${offices.slice(0, 3).join(", ")}${offices.length > 3 ? " and others" : ""}. ${gisCounties.length > 0 ? `${gisCounties.length} of those counties publish a free public parcel viewer you can check before you order title work.` : "Recorded instruments are the authoritative source; county parcel viewers, where they exist, are only a starting point."}`
        : `Easements in ${law.stateName} are recorded with the county recorder or clerk where the land sits, and they travel with the property when it sells. The recorded instrument, not the plat or the parcel viewer, controls the width and location.`,
  });

  faq.push({
    question: `Does a ${law.stateName} title commitment list every easement on the property?`,
    answer: `Schedule B lists the recorded exceptions the title company found, which covers most utility, access and drainage easements. It will not show a prescriptive easement created by ${law.years} years of use with nothing on paper, and it rarely tells you where on the lot the easement actually sits. That takes the recorded instrument plus a survey.`,
  });

  const statute = statutoryExcerptFor(law.stateCode);
  if (statute) {
    faq.push({
      question: `Which ${law.stateName} law actually sets the clock?`,
      answer: statute.excerpt
        ? `${statute.sourceSection}. In its own words: "${statute.excerpt}" We transcribed that from the official text on ${new Date(statute.checkedOn + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}.`
        : `${statute.sourceSection}. ${statute.kind === "case-law" ? `In ${law.stateName} the operative rule is judge-made rather than written into a single statute, so the period comes from decided cases reading that limitations section.` : "We link the official text rather than quote it, because we could not confirm a clean verbatim sentence from a free official source."}`,
    });
    faq.push({
      question: `What does it cost to record an easement in ${law.stateName}?`,
      answer: `${law.stateName} records with the ${statute.recordingOffice}. ${statute.recordingFeeNote}`,
    });
  }

  const cases = casesForState(law.stateCode);
  if (cases.length > 0) {
    const example = cases[0]!;
    faq.push({
      question: `What have ${law.stateName} courts paid landowners for an easement?`,
      answer: `${example.caseName} (${example.year}) is one published example: ${example.corridorDescription}. ${example.totalAward !== null ? `The total award was $${example.totalAward.toLocaleString("en-US")}${example.severanceDamages !== null ? `, of which $${example.severanceDamages.toLocaleString("en-US")} was damage to the land left over rather than the strip itself` : ""}.` : ""} ${cases.length > 1 ? `We summarize ${cases.length} ${law.stateName} decisions with stated dollar figures on the valuation calculator.` : ""}`.trim(),
    });
  }

  return faq;
}

export interface StateStatuteContext {
  excerpt: string | null;
  sourceSection: string;
  kind: StatuteExcerptKind;
  sourceUrl: string;
  checkedOn: string;
  recordingOffice: string;
  recordingFeeNote: string;
  recordingFeeUrl: string;
  /** Plain-English framing written from this state's own numbers. */
  narrative: string;
}

/**
 * The verbatim statute (or controlling case) behind this state's period, plus
 * where instruments get recorded and what that costs. This is the block that
 * makes each state page carry facts no other state page carries.
 */
export function statuteContextForState(law: StateEasementLaw): StateStatuteContext | null {
  const statute = statutoryExcerptFor(law.stateCode);
  if (!statute) return null;

  const narrative =
    statute.kind === "case-law"
      ? `${law.stateName} does not spell the prescriptive-easement period out in a single statute. The ${law.years}-year clock comes from decided cases reading ${statute.sourceSection}, which means the elements a judge will hold you to are found in the opinions rather than in the code book.`
      : `${law.stateName} writes the rule into its code at ${statute.sourceSection}. Read the operative sentence closely: the language a court applies is the statutory language, not a summary of it, and small phrases such as "under a claim of right" or "paid all the taxes" decide real cases.`;

  return {
    excerpt: statute.excerpt,
    sourceSection: statute.sourceSection,
    kind: statute.kind,
    sourceUrl: statute.sourceUrl,
    checkedOn: statute.checkedOn,
    recordingOffice: statute.recordingOffice,
    recordingFeeNote: statute.recordingFeeNote,
    recordingFeeUrl: statute.recordingFeeUrl,
    narrative,
  };
}

/** Published decisions with stated dollar figures arising in this state. */
export function valuationCasesForState(law: StateEasementLaw): EasementValuationCase[] {
  return casesForState(law.stateCode);
}

/** Editorial depth profiles exist only for the researched priority jurisdictions. */
export function depthProfileForState(law: StateEasementLaw): EnhancedStateLawProfile | null {
  return enhancedStateLawProfileFor(law.stateCode);
}

export function stateLawArticleJsonLd(law: StateEasementLaw): string {
  const profile = depthProfileForState(law);
  const path = `/easement-law/${law.stateName.toLowerCase().replace(/\s+/g, "-")}`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${law.stateName} prescriptive easement law`,
    description: profile?.distinctiveRule ?? `A practical guide to prescriptive easements in ${law.stateName}.`,
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.easements.com${path}` },
    author: { "@type": "Organization", name: "Easements.com" },
    publisher: { "@type": "Organization", name: "Easements.com" },
    about: [
      "Prescriptive easements",
      `${law.stateName} real property law`,
      ...(profile?.sources.map((source) => source.citation) ?? [law.citation]),
    ],
  });
}

export function stateFaqJsonLd(law: StateEasementLaw): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: stateFaq(law).map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: { "@type": "Answer", text: entry.answer },
    })),
  });
}

export { countyGuidePath };
